import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Layers, Search } from 'lucide-react';

export default function CreateTopicModal() {
    const { showCreateTopic, setShowCreateTopic, createTopic, currentUser, users, hasPermission } = useApp();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [color, setColor] = useState('#2196f3');
    const [allowInvites, setAllowInvites] = useState(true);
    const [encryptionMode, setEncryptionMode] = useState<'optional' | 'forced' | 'disabled'>('optional');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const isAdmin = hasPermission('admin:view') || hasPermission('super:admin');
    const [selectedUsers, setSelectedUsers] = useState<string[]>(isAdmin ? [] : [currentUser?.id || '']);
    const [searchQuery, setSearchQuery] = useState('');

    const filteredUsers = users.filter(u =>
        (u.id !== currentUser?.id || isAdmin) &&
        (u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (u.email && u.email.toLowerCase().includes(searchQuery.toLowerCase())))
    );

    const toggleUser = (userId: string) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const AVATAR_COLORS = [
        '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3',
        '#03a9f4', '#00bcd4', '#009688', '#4caf50', '#8bc34a',
        '#ff9800', '#ff5722', '#795548', '#607d8b', '#f44336'
    ];

    const handleClose = () => {
        setShowCreateTopic(false);
        setName('');
        setDescription('');
        setColor('#2196f3');
        setAllowInvites(true);
        setEncryptionMode('optional');
        setError('');
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        if (showCreateTopic) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showCreateTopic]);

    if (!showCreateTopic) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;

        setIsSubmitting(true);
        try {
            setError('');
            await createTopic({
                name,
                description,
                color,
                member_ids: selectedUsers,
                allow_member_invites: allowInvites,
                encryption_mode: encryptionMode
            });
            handleClose();
        } catch (err: any) {
            console.error('Failed to create topic:', err);
            setError(err.message || 'Failed to create topic');
        }
        setIsSubmitting(false);
    };

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal" style={{ display: 'flex', flexDirection: 'column', maxWidth: '680px' }} onClick={e => e.stopPropagation()}>
                <header className="modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                        <div style={{
                            padding: 8,
                            background: `rgba(33, 150, 243, 0.1)`,
                            borderRadius: 'var(--radius-md)',
                            color: color
                        }}>
                            <Layers size={20} />
                        </div>
                        <h2 className="modal-title">Create New Topic</h2>
                    </div>
                    <button className="btn btn-ghost btn-icon" onClick={handleClose}>✕</button>
                </header>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    {error && (
                        <div className="auth-error" style={{ margin: 'var(--spacing-md) var(--spacing-md) 0' }}>
                            {error}
                        </div>
                    )}
                    <div className="modal-body" style={{ flex: 1, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-lg)' }}>
                        {/* Left Column: Primary Settings */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">Topic Name</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Q1 Product Launch"
                                    autoFocus
                                />
                            </div>

                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">Description</label>
                                <textarea
                                    className="input"
                                    style={{ minHeight: '60px', maxHeight: '120px', resize: 'vertical' }}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="What's this topic about?"
                                    rows={2}
                                />
                            </div>

                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">Encryption</label>
                                <select
                                    className="input"
                                    value={encryptionMode}
                                    onChange={(e) => setEncryptionMode(e.target.value as any)}
                                >
                                    <option value="optional">Optional — user choice</option>
                                    <option value="forced">Forced — encrypted only</option>
                                    {isAdmin && <option value="disabled">Disabled</option>}
                                </select>
                            </div>

                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--spacing-sm)',
                                    cursor: 'pointer',
                                    marginTop: 'var(--spacing-xs)'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={allowInvites}
                                        onChange={(e) => setAllowInvites(e.target.checked)}
                                    />
                                    <span style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>Allow members to invite others</span>
                                </label>
                            </div>
                        </div>

                        {/* Right Column: Members and Color */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                            <label className="form-label" style={{ marginBottom: 0 }}>Invite Members</label>
                            <div className="modal-search-wrapper">
                                <Search size={14} className="modal-search-icon" />
                                <input
                                    className="modal-search-input"
                                    style={{ fontSize: 'var(--font-size-xs)', padding: '6px 30px' }}
                                    placeholder="Search users..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div className="modal-list-container" style={{ height: '140px' }}>
                                {filteredUsers.map(user => {
                                    const isSelected = selectedUsers.includes(user.id);
                                    return (
                                        <div
                                            key={user.id}
                                            onClick={() => toggleUser(user.id)}
                                            className={`user-list-item ${isSelected ? 'active' : ''}`}
                                            style={{ padding: '4px 8px' }}
                                        >
                                            <div className="avatar avatar-sm" style={{ width: 24, height: 24, fontSize: 10 }}>
                                                {user.name[0].toUpperCase()}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 500, fontSize: 'var(--font-size-xs)' }}>
                                                    {user.name}
                                                </div>
                                            </div>
                                            {isSelected && <span style={{ color: 'var(--accent-primary)', fontSize: 12 }}>✓</span>}
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ marginTop: 'var(--spacing-xs)' }}>
                                <label className="form-label" style={{ fontSize: 'var(--font-size-xs)', marginBottom: '4px' }}>Theme Color</label>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {AVATAR_COLORS.map(c => (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => setColor(c)}
                                            style={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                background: c,
                                                border: '2px solid transparent',
                                                cursor: 'pointer',
                                                padding: 0,
                                                boxShadow: color === c ? `0 0 0 1px var(--bg-primary), 0 0 0 3px ${c}` : 'none',
                                                transition: 'transform 0.2s'
                                            }}
                                            title={c}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer" style={{ flexShrink: 0 }}>
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!name.trim() || isSubmitting}
                        >
                            {isSubmitting ? 'Creating...' : 'Create Topic'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
